import React, { useMemo, useRef, useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, CaretDown, Check, UserPlus, X } from "@phosphor-icons/react";

/**
 * Drop-in replacement for a Shadcn <Select> customer picker with:
 *  - Live search (name / phone / GSTIN / email)
 *  - Optional "+ Add manually / new customer" row
 *  - Same onChange contract as Select (emits customer id OR the sentinel createValue)
 *
 * Usage:
 *   <SearchableCustomerSelect
 *     value={f.customer_id}
 *     onChange={pickCustomer}
 *     customers={customers}
 *     createValue="__new__"                 // or "__manual__" – optional
 *     createLabel="+ Add new customer…"     // optional
 *     placeholder="Choose customer"
 *     testid="pw-customer"
 *     getSubtitle={(c) => c.phone}           // optional subtitle line
 *   />
 */
export default function SearchableCustomerSelect({
  value,
  onChange,
  customers = [],
  createValue,
  createLabel,
  placeholder = "Choose customer",
  testid = "customer-picker",
  getSubtitle,
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQ("");
  }, [open]);

  const selected = useMemo(
    () => customers.find((c) => c.id === value) || null,
    [customers, value],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) => {
      const hay = `${c.name || ""} ${c.phone || ""} ${c.gstin || ""} ${c.email || ""}`.toLowerCase();
      return hay.includes(s);
    });
  }, [customers, q]);

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={
            "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 " +
            className
          }
        >
          <span className={selected ? "" : "text-muted-foreground"}>
            {selected ? (
              <>
                {selected.name}
                {getSubtitle?.(selected) ? (
                  <span className="text-muted-foreground"> · {getSubtitle(selected)}</span>
                ) : null}
              </>
            ) : (
              placeholder
            )}
          </span>
          <CaretDown size={14} className="opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px] max-w-[520px]"
        data-testid={`${testid}-popover`}
      >
        <div className="p-2 border-b border-border/60 sticky top-0 bg-background z-10">
          <div className="relative">
            <MagnifyingGlass
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${customers.length} customer${customers.length === 1 ? "" : "s"}…`}
              className="pl-7 pr-7 h-8 text-[13px]"
              data-testid={`${testid}-search`}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[280px] overflow-y-auto py-1">
          {createValue && createLabel && (
            <button
              type="button"
              onClick={() => pick(createValue)}
              className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-primary hover:bg-primary/5 border-b border-border/40 font-medium"
              data-testid={`${testid}-create`}
            >
              <UserPlus size={14} weight="bold" /> {createLabel}
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
              No customer matches "<b>{q}</b>".
              {createValue && (
                <div className="mt-1.5 text-[11px]">Use "{createLabel}" above to add one.</div>
              )}
            </div>
          ) : (
            filtered.map((c) => {
              const isSelected = c.id === value;
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => pick(c.id)}
                  className={`w-full text-left px-3 py-2 text-[13px] hover:bg-accent flex items-center gap-2 ${
                    isSelected ? "bg-accent/60" : ""
                  }`}
                  data-testid={`${testid}-option-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{c.name}</div>
                    {getSubtitle?.(c) && (
                      <div className="text-[11px] text-muted-foreground truncate font-mono">
                        {getSubtitle(c)}
                      </div>
                    )}
                  </div>
                  {isSelected && <Check size={13} weight="bold" className="text-primary shrink-0" />}
                </button>
              );
            })
          )}
        </div>
        {filtered.length > 0 && (
          <div className="px-3 py-1.5 text-[10.5px] text-muted-foreground border-t border-border/40 bg-muted/30 font-mono">
            {filtered.length} of {customers.length} shown
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
